/**
 * One customer: their details, their sites and their people (CRM-1).
 *
 * Everyone can look — a technician needs the gate code note and the site
 * contact's number — and the owner and office can change things.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { money, titleCase } from "../lib/format";
import { ContractorRates } from "../components/ContractorRates";
import { Card, Chip, ErrorState, Loading, PageHeader } from "../components/ui";
import { type ContractorText, contractorBody, contractorTextFrom } from "../lib/servicerates";
import type { ContactIn, ContactOut, CustomerDetail as Customer, CustomerType, SiteIn, SiteRow } from "../lib/types";
import { CUSTOMER_TYPES } from "./Customers";

type Said = { tone: "info" | "crit"; text: string } | null;

export default function CustomerDetail() {
  const { id = "" } = useParams();
  const { can } = useAuth();
  const canEdit = can(...OFFICE);
  const queryClient = useQueryClient();
  const customer = useQuery({ queryKey: ["customer", id], queryFn: () => api.customers.get(id) });
  const [said, setSaid] = useState<Said>(null);

  const adopt = (next: Customer) => {
    queryClient.setQueryData(["customer", id], next);
    void queryClient.invalidateQueries({ queryKey: ["customers"] });
    void queryClient.invalidateQueries({ queryKey: ["sites"] });
  };
  const fail = (err: unknown) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't save that." });

  if (customer.isPending) return <Loading what="Loading the customer" />;
  if (customer.error) return <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />;
  const c = customer.data;

  return (
    <div className="page">
      <PageHeader
        eyebrow={titleCase(c.type)}
        title={c.name}
        actions={
          canEdit &&
          c.sites.length > 0 && (
            <span className="btn-row">
              <Link className="btn" to={`/quotes/new?customer=${c.id}`}>
                New quote
              </Link>
              <Link className="btn btn-primary" to={`/jobs/new?customer=${c.id}`}>
                New job
              </Link>
            </span>
          )
        }
      >
        {[c.abn ? `ABN ${c.abn}` : null, c.owing_cents > 0 ? `${money(c.owing_cents)} owing` : null, c.in_xero ? "In Xero" : null]
          .filter(Boolean)
          .join(" · ") || "No invoices yet"}
        {c.on_credit_hold && (
          <>
            {" "}
            <Chip tone="crit">Credit hold</Chip>
          </>
        )}
      </PageHeader>

      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
          {said.text}
        </p>
      )}

      <div className="split">
        <div className="split-main">
          <SitesCard c={c} canEdit={canEdit} onChanged={adopt} onError={fail} />
          <ContactsCard c={c} canEdit={canEdit} onChanged={adopt} onError={fail} />
        </div>
        <aside className="split-side">
          <DetailsCard c={c} canEdit={canEdit} onChanged={(next) => { adopt(next); setSaid({ tone: "info", text: "Saved." }); }} onError={fail} />
        </aside>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- details */

function DetailsCard({
  c,
  canEdit,
  onChanged,
  onError,
}: {
  c: Customer;
  canEdit: boolean;
  onChanged: (next: Customer) => void;
  onError: (err: unknown) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: c.name,
    type: c.type,
    abn: c.abn ?? "",
    billing_address: c.billing_address ?? "",
    account_terms_days: String(c.account_terms_days),
    on_credit_hold: c.on_credit_hold,
    notes: c.notes ?? "",
  });
  const [isContractor, setIsContractor] = useState(Boolean(c.is_contractor));
  const [rates, setRates] = useState<ContractorText>(contractorTextFrom(c));
  // null while a rate isn't valid money, which holds Save back.
  const contractor = contractorBody(isContractor, rates);
  const save = useMutation({
    mutationFn: () => {
      // Built as a variable: `api.customers.update`'s declared body predates the contractor fields.
      const body = {
        name: form.name,
        type: form.type,
        abn: form.abn,
        billing_address: form.billing_address,
        account_terms_days: Number(form.account_terms_days) || 0,
        on_credit_hold: form.on_credit_hold,
        notes: form.notes,
        ...contractor,
      };
      return api.customers.update(c.id, body);
    },
    onSuccess: (next) => {
      setEditing(false);
      onChanged(next);
    },
    onError,
  });

  if (!editing) {
    return (
      <Card
        title="Details"
        actions={
          canEdit && (
            <button className="linkish tiny" type="button" onClick={() => setEditing(true)}>
              Edit
            </button>
          )
        }
      >
        <dl className="kv">
          <dt>Billing address</dt>
          <dd style={{ whiteSpace: "pre-line" }}>{c.billing_address || <span className="muted">The site's</span>}</dd>
          <dt>Terms</dt>
          <dd>{c.account_terms_days} days</dd>
          {c.is_contractor && (
            <>
              <dt>Contractor</dt>
              <dd>
                {[
                  ["fee", c.contractor_fee_cents],
                  ["hourly", c.contractor_hourly_rate_cents],
                  ["per km", c.contractor_km_rate_cents],
                ]
                  .map(([label, cents]) => `${label} ${cents === null || cents === undefined ? "business rate" : money(cents as number)}`)
                  .join(" · ")}
              </dd>
            </>
          )}
          {c.notes && (
            <>
              <dt>Notes</dt>
              <dd style={{ whiteSpace: "pre-line" }}>{c.notes}</dd>
            </>
          )}
        </dl>
      </Card>
    );
  }

  return (
    <Card title="Details">
      <div className="stack">
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as CustomerType })}>
            {CUSTOMER_TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>ABN</span>
          <input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
        </label>
        <label className="field">
          <span>Billing address (if not the site)</span>
          <textarea rows={3} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
        </label>
        <label className="field">
          <span>Payment terms (days)</span>
          <input inputMode="numeric" value={form.account_terms_days} onChange={(e) => setForm({ ...form, account_terms_days: e.target.value })} />
        </label>
        <label className="tiny" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={form.on_credit_hold} onChange={(e) => setForm({ ...form, on_credit_hold: e.target.checked })} />
          On credit hold
        </label>
        <ContractorRates
          isContractor={isContractor}
          text={rates}
          onToggle={setIsContractor}
          onChange={(key, value) => setRates((r) => ({ ...r, [key]: value }))}
        />
        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>
        <div className="btn-row">
          <button className="btn btn-primary" type="button" disabled={!form.name.trim() || contractor === null || save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
          <button className="btn" type="button" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------- sites */

function SitesCard({
  c,
  canEdit,
  onChanged,
  onError,
}: {
  c: Customer;
  canEdit: boolean;
  onChanged: (next: Customer) => void;
  onError: (err: unknown) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (body: SiteIn) => api.customers.addSite(c.id, body),
    onSuccess: (next) => {
      setAdding(false);
      onChanged(next);
    },
    onError,
  });
  const update = useMutation({
    mutationFn: ({ siteId, body }: { siteId: string; body: SiteIn }) => api.customers.updateSite(siteId, body),
    onSuccess: (next) => {
      setEditing(null);
      onChanged(next);
    },
    onError,
  });

  return (
    <Card title="Sites" subtitle="Where the work is — jobs and hardware hang off these">
      {c.sites.length === 0 && <p className="tiny muted">No sites yet. Add one before booking a job.</p>}
      <div className="stack">
        {c.sites.map((s) =>
          editing === s.id ? (
            <SiteForm key={s.id} initial={s} busy={update.isPending} onCancel={() => setEditing(null)} onSave={(body) => update.mutate({ siteId: s.id, body })} />
          ) : (
            <div key={s.id} className="doc-row" style={{ alignItems: "flex-start" }}>
              <div>
                <Link to={`/sites/${s.id}`}>
                  <b>{s.name}</b>
                </Link>
                <div className="tiny muted">{addressOf(s)}</div>
                {s.access_notes && <div className="tiny" style={{ whiteSpace: "pre-line", marginTop: 2 }}>{s.access_notes}</div>}
                <div className="tiny" style={{ marginTop: 2 }}>
                  {s.induction_required && <Chip tone="warn">Induction</Chip>} {s.swms_required && <Chip tone="warn">SWMS</Chip>}{" "}
                  {s.open_job_count > 0 && <span className="muted">{s.open_job_count} open job{s.open_job_count === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <span className="doc-actions">
                {canEdit && (
                  <>
                    <Link className="linkish tiny" to={`/jobs/new?customer=${c.id}&site=${s.id}`}>
                      New job
                    </Link>
                    <button className="linkish tiny" type="button" onClick={() => setEditing(s.id)}>
                      Edit
                    </button>
                  </>
                )}
              </span>
            </div>
          ),
        )}
      </div>
      {canEdit &&
        (adding ? (
          <SiteForm busy={add.isPending} onCancel={() => setAdding(false)} onSave={(body) => add.mutate(body)} />
        ) : (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button className="btn" type="button" onClick={() => setAdding(true)}>
              Add a site
            </button>
          </div>
        ))}
    </Card>
  );
}

function addressOf(s: SiteIn): string {
  const town = [s.suburb, s.state, s.postcode].filter(Boolean).join(" ");
  return [s.address_line, town].filter(Boolean).join(", ") || "No address";
}

function SiteForm({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial?: SiteRow;
  busy: boolean;
  onSave: (body: SiteIn) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    address_line: initial?.address_line ?? "",
    suburb: initial?.suburb ?? "",
    state: initial?.state ?? "SA",
    postcode: initial?.postcode ?? "",
    access_notes: initial?.access_notes ?? "",
    induction_required: initial?.induction_required ?? false,
    swms_required: initial?.swms_required ?? false,
  });
  const set = (k: "name" | "address_line" | "suburb" | "state" | "postcode" | "access_notes") => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <div className="book-form" style={{ display: "block" }}>
      <div className="form-grid">
        <label className="field">
          <span>Site name (optional)</span>
          <input value={form.name} onChange={set("name")} placeholder="e.g. Loading dock" />
        </label>
        <label className="field">
          <span>Street</span>
          <input value={form.address_line} onChange={set("address_line")} />
        </label>
        <label className="field">
          <span>Suburb</span>
          <input value={form.suburb} onChange={set("suburb")} />
        </label>
        <label className="field">
          <span>State</span>
          <input value={form.state} onChange={set("state")} />
        </label>
        <label className="field">
          <span>Postcode</span>
          <input value={form.postcode} onChange={set("postcode")} inputMode="numeric" />
        </label>
      </div>
      <label className="field" style={{ marginTop: 8 }}>
        <span>Access notes — parking, keys, who to see (never the alarm code)</span>
        <textarea rows={2} value={form.access_notes} onChange={set("access_notes")} />
      </label>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={form.induction_required} onChange={(e) => setForm({ ...form, induction_required: e.target.checked })} />
          Induction required
        </label>
        <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={form.swms_required} onChange={(e) => setForm({ ...form, swms_required: e.target.checked })} />
          SWMS required
        </label>
      </div>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          disabled={busy || !(form.name.trim() || form.address_line.trim() || form.suburb.trim())}
          onClick={() => onSave(form)}
        >
          Save site
        </button>
        <button className="btn btn-sm" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- contacts */

function ContactsCard({
  c,
  canEdit,
  onChanged,
  onError,
}: {
  c: Customer;
  canEdit: boolean;
  onChanged: (next: Customer) => void;
  onError: (err: unknown) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (body: ContactIn) => api.customers.addContact(c.id, body),
    onSuccess: (next) => {
      setAdding(false);
      onChanged(next);
    },
    onError,
  });
  const update = useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: ContactIn }) => api.customers.updateContact(contactId, body),
    onSuccess: (next) => {
      setEditing(null);
      onChanged(next);
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (contactId: string) => api.customers.removeContact(contactId), onSuccess: onChanged, onError });

  return (
    <Card title="People" subtitle="Who to ring, and who gets the invoices">
      {c.contacts.length === 0 && <p className="tiny muted">Nobody yet.</p>}
      <div className="stack">
        {c.contacts.map((p) =>
          editing === p.id ? (
            <ContactForm key={p.id} initial={p} sites={c.sites} busy={update.isPending} onCancel={() => setEditing(null)} onSave={(body) => update.mutate({ contactId: p.id, body })} />
          ) : (
            <div key={p.id} className="doc-row" style={{ alignItems: "flex-start" }}>
              <div>
                <b>{p.name}</b> {p.is_primary && <Chip tone="info">Main contact</Chip>}
                {p.role && <span className="tiny muted"> · {p.role}</span>}
                <div className="tiny">
                  {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`}>{p.phone}</a>}
                  {p.phone && p.email && " · "}
                  {p.email && <a href={`mailto:${p.email}`}>{p.email}</a>}
                  {p.site_id && <span className="muted"> · {c.sites.find((s) => s.id === p.site_id)?.name}</span>}
                </div>
              </div>
              {canEdit && (
                <span className="doc-actions">
                  <button className="linkish tiny" type="button" onClick={() => setEditing(p.id)}>
                    Edit
                  </button>
                  <button
                    className="linkish tiny"
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove ${p.name}?`)) remove.mutate(p.id);
                    }}
                  >
                    Remove
                  </button>
                </span>
              )}
            </div>
          ),
        )}
      </div>
      {canEdit &&
        (adding ? (
          <ContactForm sites={c.sites} busy={add.isPending} onCancel={() => setAdding(false)} onSave={(body) => add.mutate(body)} />
        ) : (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button className="btn" type="button" onClick={() => setAdding(true)}>
              Add a person
            </button>
          </div>
        ))}
    </Card>
  );
}

function ContactForm({
  initial,
  sites,
  busy,
  onSave,
  onCancel,
}: {
  initial?: ContactOut;
  sites: SiteRow[];
  busy: boolean;
  onSave: (body: ContactIn) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    role: initial?.role ?? "",
    phone: initial?.phone ?? "",
    email: initial?.email ?? "",
    is_primary: initial?.is_primary ?? false,
    site_id: initial?.site_id ?? "",
  });
  return (
    <div className="book-form" style={{ display: "block" }}>
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Role</span>
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Accounts, Site manager" />
        </label>
        <label className="field">
          <span>Phone</span>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} inputMode="tel" />
        </label>
        <label className="field">
          <span>Email</span>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" autoCapitalize="none" />
        </label>
        {sites.length > 1 && (
          <label className="field">
            <span>At site</span>
            <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
              <option value="">Any / head office</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
        <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
        Main contact — invoices and quotes go to their email
      </label>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          disabled={busy || !form.name.trim()}
          onClick={() => onSave({ ...form, site_id: form.site_id || null })}
        >
          Save
        </button>
        <button className="btn btn-sm" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
